import { trpc, trpcUtils } from "@/hooks/trpc";
import { HarvestSessionId, HarvestCleaningPositionEnum } from "@brewbase/db/types";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { useMemo } from "react";
import { useSessionStages } from "@/features/harvest-journal/hooks/use-session-stages";
import {
  buildStageNav,
  parseStagePage,
} from "@/features/harvest-journal/lib/stage-nav-model";
import { useSidebarConfig } from "@/hooks/use-sidebar-config";
import type { SidebarNavItem } from "@/components/nav/sidebar-store";
import { Wheat } from "lucide-react";
import { CompleteSessionDialog } from "@/features/harvest-journal/components/complete-session-dialog";
import { cn } from "@/lib/utils";
import { deriveSessionName } from "@/features/harvest-journal/lib/session-name";
import { format } from "date-fns";

/**
 * Pages the session used to address with `?page=`, and where each one lives now.
 * Saved links and open tabs still carry the old form, so they are translated on
 * the way in rather than left to land on the plan.
 */
const LEGACY_PAGE_PATHS: Record<string, string> = {
  plan: "",
  "pre-cleaning": "/pre-cleaning",
  "post-cleaning": "/post-cleaning",
  samples: "/samples",
  batches: "/batches",
  live: "/live",
  analysis: "/analysis",
};

export const Route = createFileRoute("/harvests/$session_id")({
  component: RouteComponent,
  params: z.object({
    session_id: z.coerce.number().transform((val) => val as HarvestSessionId),
  }),
  validateSearch: z.object({
    page: z.string().optional(),
  }),
  beforeLoad: ({ params, search }) => {
    const page = search.page;
    if (!page) return;
    const stageId = parseStagePage(page);
    const path =
      stageId != null ? `/stages/${stageId}` : LEGACY_PAGE_PATHS[page];
    // An unknown page names nothing — drop the param and land on the plan,
    // which is the one page that can orient someone with a stale address.
    throw redirect({
      href: `/harvests/${params.session_id}${path ?? ""}`,
      replace: true,
    });
  },
  loader: async ({ params }) => {
    const { session_id } = params;
    // Stages first, alone: reading them assigns them, and an operation fetched
    // beside that read can predate the assignment it performs.
    await trpcUtils.harvest.stages.listForSession.ensureData(session_id);
    await Promise.all([
      trpcUtils.harvest.getSession.ensureData(session_id),
      trpcUtils.harvest.getOperationsForSession.ensureData(session_id),
      trpcUtils.harvest.getHarvestsInSessionWithDetails.ensureData(session_id),
    ]);
  },
});

const STATUS_COLORS: Record<string, string> = {
  PLANNED: "bg-olive-8",
  IN_PROGRESS: "bg-yellow-9",
  COMPLETED: "bg-green-9",
};

function RouteComponent() {
  const { session_id } = Route.useParams();

  const { model } = useSessionStages(session_id);

  const { data: session } = trpc.harvest.getSession.useQuery(session_id);

  const { data: harvestDetails } =
    trpc.harvest.getHarvestsInSessionWithDetails.useQuery(session_id);

  const { data: preCleaningRequirements } = trpc.harvest.getSessionModelCleaningRequirements.useQuery(
    { sessionId: session_id, position: HarvestCleaningPositionEnum.PRE },
  );
  const { data: postCleaningRequirements } = trpc.harvest.getSessionModelCleaningRequirements.useQuery(
    { sessionId: session_id, position: HarvestCleaningPositionEnum.POST },
  );
  const hasPreCleaning = (preCleaningRequirements?.length ?? 0) > 0;
  const hasPostCleaning = (postCleaningRequirements?.length ?? 0) > 0;

  const sessionLifecycleStatus = useMemo(() => {
    if (!session) return null;
    if (session.ended_at) return "COMPLETED";
    if (session.started_at) return "IN_PROGRESS";
    return "PLANNED";
  }, [session]);

  /**
   * The plan, exactly as the planner orders it. The shape of the session is
   * stored now, so the sidebar reads it rather than inferring it from what
   * depends on what — which is what let the two surfaces disagree about how many
   * stages there were and where the branches ran.
   */
  const stageItems = useMemo(
    () => buildStageNav(model, session_id),
    [model, session_id],
  );

  const navigation = useMemo((): SidebarNavItem[] => {
    const result: SidebarNavItem[] = [];

    // One front page. The plan is it, before and after the session starts —
    // there is no separate overview to land on any more, because every section
    // it held is either on the grid's column heads, on Analysis, or gone.
    result.push({
      type: "link",
      id: "plan",
      label: "Plan",
      href: `/harvests/${session_id}`,
    });

    result.push({ type: "divider", id: "divider-after-plan" });

    if (hasPreCleaning) {
      result.push({
        type: "link",
        id: "pre-cleaning",
        label: "Pre-Cleaning",
        href: `/harvests/${session_id}/pre-cleaning`,
      });
    }

    if (stageItems.length > 0) {
      result.push({
        type: "stages",
        id: "stages",
        items: stageItems,
      });
    }

    if (hasPostCleaning) {
      result.push({
        type: "link",
        id: "post-cleaning",
        label: "Post-Cleaning",
        href: `/harvests/${session_id}/post-cleaning`,
      });
    }

    result.push({ type: "divider", id: "divider-1" });

    // Add samples and batches links
    result.push({
      type: "link",
      id: "samples",
      label: "Samples",
      href: `/harvests/${session_id}/samples`,
    });
    result.push({
      type: "link",
      id: "batches",
      label: "Batches",
      href: `/harvests/${session_id}/batches`,
    });

    result.push({ type: "divider", id: "divider-analysis" });

    // Live mimic page hidden for now — needs more work. Re-enable by restoring
    // this nav entry (the /live route + LiveMimic are still wired).
    // if (isSessionStarted) {
    //   result.push({
    //     type: "link",
    //     id: "live",
    //     label: "Live",
    //     href: `/harvests/${session_id}/live`,
    //   });
    // }

    result.push({
      type: "link",
      id: "analysis",
      label: "Analysis",
      href: `/harvests/${session_id}/analysis`,
    });

    return result;
  }, [stageItems, session_id, hasPreCleaning, hasPostCleaning]);

  // Set sidebar config
  const sessionName = useMemo(
    () =>
      deriveSessionName({
        id: session_id,
        planned_date: session?.planned_date ?? null,
        started_at: session?.started_at ?? null,
        responsible_operator_name: session?.responsible_operator_name ?? null,
        harvests: harvestDetails ?? null,
        label: session?.label ?? null,
      }),
    [session_id, session, harvestDetails],
  );
  useSidebarConfig({
    layer: 2,
    parent: { label: "Harvests", icon: Wheat, href: "/harvests" },
    customHeader: (
      <div className="px-3 py-2 border-b border-olive-6">
        <div>
          <div className="font-medium text-sm truncate">{sessionName}</div>
        </div>
        <div className="flex items-center gap-2 mt-2">
          {sessionLifecycleStatus && (
            <span className="flex items-center gap-1.5 text-xs text-olive-11">
              <span className={cn("w-1.5 h-1.5 rounded-full", STATUS_COLORS[sessionLifecycleStatus])} />
              {sessionLifecycleStatus === "PLANNED" ? "Planned" : sessionLifecycleStatus === "IN_PROGRESS" ? "In progress" : "Completed"}
            </span>
          )}
          {session?.started_at && (
            <span className="text-xs text-olive-9">
              Started {format(new Date(session.started_at), "d MMM HH:mm")}
            </span>
          )}
        </div>
        {!session?.ended_at && harvestDetails && (
          <div className="mt-2">
            <CompleteSessionDialog
              sessionId={session_id}
              harvestsWithDetails={harvestDetails}
            />
          </div>
        )}
      </div>
    ),
    navigation,
    variant: "complex",
  });

  return (
    // Stage journals manage their own width (full-width header + constrained
    // body); every other page wraps itself in JournalPage.
    <div className="h-full w-full overflow-auto">
      <Outlet />
    </div>
  );
}
